import React from "react"
import PropTypes from "prop-types"
import Grid from "./Grid"
import { getPath, getCodeOrConcept } from "../../lib"
import Period from "./Period"
import Date from "./Date"
import moment from "moment"
import { Eye } from "lucide-react"

function renderCell(record, allOf, oneOf, handleDocumentClick) {

    const entries = [];

    const visit = meta => {
        let propValue = getPath(record, meta.path);
        if (propValue !== undefined) {
            let value = meta.render ? meta.render(record) : propValue;
            let raw = meta.raw ? meta.raw(record) : value;
            let label = typeof meta.label == "function" ? meta.label(record) : meta.label;
            let existing = entries.find(o => o.value === raw);
            if (existing) {
                existing.label += ", " + label
            }
            else {
                entries.push({ label, value });
            }
            return true;
        }
        return false;
    };

    allOf.forEach(visit);

    if (!entries.length) {
        oneOf.some(visit);
    }

    if (entries.length) {
        return (
            <table>
                <tbody>
                    {
                        entries.map((o, i) => (
                            <tr key={i}>
                                {/* <td className="label-cell">{o.label}</td> */}
                                <td>{o.value}</td>
                            </tr>
                        ))
                    }
                </tbody>
            </table>
        );
    }

    return "-"
}

function getSortValue(rec) {
    const paths = [
        "authoredOn",
        "dateWritten",
        "performedDateTime",
        "recorded",
        "performedPeriod.start",
        "availableTime.0.availableStartTime",
        "whenHandedOver",
        "issued",
        "effectiveDateTime",
        "assertedDate",
        "meta.lastUpdated"
    ];

    for (let i = 0, l = paths.length; i < l; i++) {
        let v = getPath(rec, paths[i]);
        if (v !== undefined) {
            return v
        }
    }

    return 0;
}

function DataLink({ settings, path, children }) {
    let url = `${settings.server.url}/${path}`;
    if (settings.fhirViewer.enabled) {
        url = settings.fhirViewer.url +
            (settings.fhirViewer.url.indexOf("?") > -1 ? "&" : "?") +
            settings.fhirViewer.param + "=" +
            encodeURIComponent(url);
    }
    return <a onClick={() => {
        window.open(url, "_blank");
        return false;
    }}>{children}</a>
}

DataLink.propTypes = {
    settings: PropTypes.object.isRequired,
    path: PropTypes.string.isRequired,
    children: PropTypes.any
}

export default class ServiceRequest extends React.Component {
    static propTypes = {
        type: PropTypes.string,
        resources: PropTypes.arrayOf(PropTypes.object).isRequired,
        settings: PropTypes.object.isRequired
    };

    /**
     * The idea is to have 3 columns by default:
     * 1. Identifier - something like a name, type, id etc.
     * 2. Details - the important part that will describe the resource
     * 3. Timing - time period, time, age etc.
     * @returns {Array} The generated schema
     */
    determineSchema(sampleResource) {
        const out = [];

        if (!sampleResource) {
            return out;
        }

        // 2. Details ----------------------------------------------------------
        out.push({
            label: "Reason",
            path: "reasonCode.0.text",
            render: rec => {
                return rec.reasonCode?.[0]?.text;
            },
        });
        out.push({
            label: "Code",
            render: rec => {
                return rec.code.text;
            },
        });
        out.push({
            label: "Category",
            path: "category",
            render: rec => {
                function renderCategory(c) {
                    if (!c) {
                        return "N/A";
                    }

                    if (typeof c == "string") {
                        return c;
                    }

                    if (Array.isArray(c)) {
                        return c.map(renderCategory).join(", ");
                    }

                    if (typeof c != "object") {
                        return "N/A";
                    }

                    return c.text || c.display || c.code || renderCategory(c.coding);
                }

                return renderCategory(rec.category);
            }
        });
        // 2. Timings ----------------------------------------------------------
        out.push({
            label: "Date",
            render: o => renderCell(o, [
                {
                    path: "authoredOn",
                    label: "Authored On",
                    render: rec => <Date moment={rec.authoredOn} />
                },
                {
                    path: "dateWritten",
                    label: "Date Written",
                    render: rec => <Date moment={rec.dateWritten} />
                },
                {
                    path: "performedDateTime",
                    label: "Performed At",
                    render: rec => <Date moment={rec.performedDateTime} />
                },
                {
                    path: "recorded",
                    label: "Recorded",
                    render: rec => <Date moment={rec.recorded} />
                },
                {
                    path: "performedPeriod",
                    label: "Performed",
                    render: rec => Period(rec.performedPeriod)
                },
                {
                    path: "availableTime",
                    label: "Available Time",
                    render: rec => {
                        if (Array.isArray(rec.availableTime)) {
                            return rec.availableTime.map((t, i) => (
                                <div key={i}>
                                    {t.availableStartTime && (<span>
                                        <label>from:&nbsp;</label>
                                        <span>{t.availableStartTime}&nbsp;</span>
                                    </span>)}
                                    {t.availableEndTime && (<span>
                                        <label>to:&nbsp;</label>
                                        <span>{t.availableEndTime}</span>
                                    </span>)}
                                </div>
                            ));
                        }
                    }
                },
                {
                    path: "whenHandedOver",
                    label: "Handed Over",
                    render: rec => <Date moment={rec.whenHandedOver} />
                },
                {
                    path: "issued",
                    label: "Issued",
                    raw: rec => moment(rec.issued).format("MM/DD/YYYY"),
                    render: rec => <Date moment={rec.issued} />
                },
                {
                    path: "effectiveDateTime",
                    label: "Effective",
                    raw: rec => moment(rec.effectiveDateTime).format("MM/DD/YYYY"),
                    render: rec => <Date moment={rec.effectiveDateTime} />
                },
                {
                    path: "assertedDate",
                    label: "Asserted",
                    raw: rec => rec.assertedDate,
                    render: rec => <Date moment={rec.assertedDate} />
                }
            ], [
                {
                    path: "meta.lastUpdated",
                    label: "Last Updated",
                    raw: rec => rec.meta.lastUpdated,
                    render: rec => <Date moment={rec.meta.lastUpdated} />
                }
            ])
        });

        return out;
    }

    render() {
        console.log("Rendering ServiceRequest with resources", this.props.resources, this.props.type);
        let recs = this.props.resources || []
        let length = recs.length;
        return (
            <Grid
                rows={recs.map(o => o.resource)}
                title={`${length} resource${length === 1 ? "" : "s"} of type ${this.props.type}`}
                cols={this.determineSchema(recs[0].resource)}
                groupBy="Reason"
                comparator={(a, b) => {
                    let dA = getSortValue(a);
                    let dB = getSortValue(b);
                    dA = dA ? +moment(dA) : 0;
                    dB = dB ? +moment(dB) : 0;
                    return dB - dA;
                }}
            />
        )
    }
}

